import { isNil } from 'lodash';
import {
  memo,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { areEqualFields, onKeyDown } from 'utils/form';
import requireValue from 'utils/requireValue';
import type { TFieldComponentProps } from './types';

export interface ITextInputProps extends TFieldComponentProps<string | null | undefined> {
  label?: ReactNode;
  type?: string;
  style?: CSSProperties;
  required?: boolean;
  endIcon?: ReactNode;
  skipScrollingToField?: boolean;
  lowerCase?: boolean;
}

const TextInput = ({
  field, // { name, value, onChange, onBlur }
  form: { touched, errors }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  required,
  endIcon,
  skipScrollingToField,
  lowerCase,
  ...props
}: ITextInputProps) => {
  const { label, type = 'text', style } = props;
  const { value, name } = field;
  const getClassNameForInput =
    touched[field.name] && errors[field.name] ? 'error-input default-input' : 'default-input';

  const fieldWrapperRef = useRef<HTMLDivElement>(null);
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (value) onKeyDown(e, fieldWrapperRef);
    },
    [value],
  );

  const fieldWrapperClassnames = useMemo(() => {
    let classes = 'field-wrapper';
    if (!skipScrollingToField) classes += ' scroll-to-field';
    return classes;
  }, [skipScrollingToField]);

  const inputValue = useMemo(() => {
    if (lowerCase) return requireValue(value, 'toLowerCase').toLowerCase();
    return !isNil(value) ? value : '';
  }, [lowerCase, value]);

  return (
    <div className={fieldWrapperClassnames} style={style} ref={fieldWrapperRef}>
      {endIcon ? (
        <span style={{ position: 'absolute', right: '5px', bottom: '23px' }}>{endIcon}</span>
      ) : null}
      <input
        {...field}
        value={inputValue}
        type={type}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        className={getClassNameForInput}
        id={name}
        required={required}
        maxLength={64}
        style={endIcon ? { paddingRight: '35px' } : {}}
      />
      <label
        className={value ? 'label-when-focused-input' : 'text-label'}
        htmlFor={name}
        id={`label-${name}`}
      >
        {label}
      </label>
      {errors[name] && touched[name] && <div className="error-text">{errors[name]}</div>}
    </div>
  );
};

export default memo(TextInput, (prevProps, nextProps) =>
  areEqualFields(prevProps, nextProps, ['type']),
);
